import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';

type Preview = Awaited<ReturnType<typeof api.admin.ogPreview>>;

const PRESETS = [
  { label: 'Home', path: '/' },
  { label: 'Pricing for PLG (upcoming)', path: '/w/pricing-for-product-led-growth-a1b2c3' },
  { label: 'AI-native team (upcoming)', path: '/w/building-an-ai-native-team-d4e5f6' },
  { label: 'Growth loops (past)', path: '/w/growth-loops-that-compound-m3n4o5' },
  { label: 'Draft (should fall back)', path: '/w/eval-driven-development-draft-x9y0z1' },
  { label: 'Bad slug (should fall back)', path: '/w/does-not-exist' },
];

export function AdminPreviewPage() {
  const [path, setPath] = useState('/');
  const query = useQuery({
    queryKey: ['admin', 'og-preview', path],
    queryFn: () => api.admin.ogPreview(path),
  });

  const p = query.data;

  // Prefer OG-specific fields, fall back to base meta. Mirrors what crawlers do.
  const card = useMemo(() => {
    if (!p) return null;
    const title = p.ogTitle ?? p.title ?? '';
    const description = p.ogDescription ?? p.description ?? '';
    const image = p.ogImage ?? p.twitterImage ?? null;
    const domain = (() => {
      try {
        return new URL(p.ogUrl ?? p.url).hostname.replace(/^www\./, '');
      } catch {
        return '';
      }
    })();
    return { title, description, image, domain, url: p.ogUrl ?? p.url };
  }, [p]);

  return (
    <div className="container-page max-w-4xl py-10">
      <Link to="/admin" className="text-sm text-ink-muted hover:text-ink">← Back to admin</Link>
      <h1 className="mt-3 text-2xl font-semibold tracking-tight">Social preview</h1>
      <p className="mt-1.5 text-sm text-ink-muted">
        See exactly what shows up when you share a link on Twitter/X, LinkedIn, Slack, or iMessage.
        Cards render the OG/Twitter meta tags returned by this server right now.
      </p>

      {/* Path picker */}
      <div className="mt-8 rounded-2xl border border-line bg-surface p-5 shadow-card">
        <label className="block">
          <span className="text-xs uppercase tracking-wide text-ink-subtle">Path</span>
          <div className="mt-1 flex gap-2">
            <input
              type="text"
              className="input flex-1 font-mono text-sm"
              value={path}
              onChange={(e) => setPath(e.target.value)}
              placeholder="/w/some-slug"
            />
            <button
              type="button"
              onClick={() => query.refetch()}
              className="btn-secondary"
              disabled={query.isFetching}
            >
              {query.isFetching ? 'Loading…' : 'Refresh'}
            </button>
          </div>
        </label>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {PRESETS.map((preset) => (
            <button
              key={preset.path}
              type="button"
              onClick={() => setPath(preset.path)}
              className={`chip-pill cursor-pointer transition ${
                path === preset.path ? 'border-ink text-ink' : 'hover:border-ink/40 hover:text-ink'
              }`}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      {query.isLoading ? (
        <p className="mt-8 text-sm text-ink-subtle">Loading preview…</p>
      ) : query.isError ? (
        <p className="mt-8 text-sm text-red-600">
          Failed to load preview: {String(query.error)}
        </p>
      ) : card ? (
        <>
          {/* Raw meta dump */}
          <RawMetaPanel data={p!} />

          {/* Platform cards */}
          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <TwitterCard card={card} />
            <LinkedInCard card={card} />
            <SlackCard card={card} />
            <IMessageCard card={card} />
          </div>
        </>
      ) : null}
    </div>
  );
}

type CardData = {
  title: string;
  description: string;
  image: string | null;
  domain: string;
  url: string;
};

/* --------------------------- Twitter / X card ----------------------------- */

function TwitterCard({ card }: { card: CardData }) {
  return (
    <PlatformShell label="Twitter / X" sub="Summary large image card">
      <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white">
        {card.image ? (
          <div className="aspect-[1.91/1] w-full overflow-hidden bg-neutral-100">
            <img src={card.image} alt="" className="h-full w-full object-cover" />
          </div>
        ) : null}
        <div className="border-t border-neutral-100 px-3 py-2">
          <p className="text-[12px] text-neutral-500">From {card.domain}</p>
          <p className="mt-0.5 line-clamp-1 text-[15px] font-medium leading-snug text-neutral-900">
            {card.title || '(no title)'}
          </p>
          <p className="line-clamp-2 text-[13px] leading-snug text-neutral-500">
            {card.description || '(no description)'}
          </p>
        </div>
      </div>
    </PlatformShell>
  );
}

/* ---------------------------- LinkedIn card ------------------------------ */

function LinkedInCard({ card }: { card: CardData }) {
  return (
    <PlatformShell label="LinkedIn" sub="Feed link card">
      <div className="overflow-hidden rounded-md border border-neutral-300 bg-white">
        {card.image ? (
          <div className="aspect-[1.91/1] w-full overflow-hidden bg-neutral-100">
            <img src={card.image} alt="" className="h-full w-full object-cover" />
          </div>
        ) : null}
        <div className="border-t border-neutral-200 px-4 py-3">
          <p className="line-clamp-2 text-[15px] font-semibold leading-snug text-neutral-900">
            {card.title || '(no title)'}
          </p>
          <p className="mt-1 text-[12px] uppercase tracking-wide text-neutral-500">{card.domain} • 1 min read</p>
        </div>
      </div>
    </PlatformShell>
  );
}

/* ------------------------- Slack / Discord card -------------------------- */

function SlackCard({ card }: { card: CardData }) {
  return (
    <PlatformShell label="Slack" sub="Channel unfurl">
      <div className="rounded-md border border-l-4 border-neutral-200 border-l-neutral-400 bg-neutral-50 p-3">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-[12px] text-neutral-500">{card.domain}</p>
            <p className="mt-0.5 text-[14px] font-bold leading-tight text-blue-700">
              {card.title || '(no title)'}
            </p>
            <p className="mt-1 line-clamp-3 text-[13px] leading-snug text-neutral-700">
              {card.description || '(no description)'}
            </p>
          </div>
          {card.image ? (
            <img
              src={card.image}
              alt=""
              className="h-20 w-20 shrink-0 rounded-md object-cover"
            />
          ) : null}
        </div>
      </div>
    </PlatformShell>
  );
}

/* ----------------------------- iMessage card ----------------------------- */

function IMessageCard({ card }: { card: CardData }) {
  return (
    <PlatformShell label="iMessage" sub="Rich link bubble (light mode)">
      <div className="inline-block max-w-[280px] overflow-hidden rounded-2xl bg-neutral-200">
        {card.image ? (
          <div className="aspect-[1.91/1] w-full overflow-hidden">
            <img src={card.image} alt="" className="h-full w-full object-cover" />
          </div>
        ) : null}
        <div className="px-3 py-2">
          <p className="line-clamp-2 text-[13px] font-semibold leading-tight text-neutral-900">
            {card.title || '(no title)'}
          </p>
          <p className="text-[11px] text-neutral-500">{card.domain}</p>
        </div>
      </div>
    </PlatformShell>
  );
}

/* ---------------------------- Shell + meta panel ------------------------- */

function PlatformShell({
  label,
  sub,
  children,
}: {
  label: string;
  sub: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-line bg-surface p-5 shadow-card">
      <div className="mb-3 flex items-baseline justify-between">
        <h2 className="text-sm font-semibold tracking-tight">{label}</h2>
        <span className="text-[11px] text-ink-subtle">{sub}</span>
      </div>
      {children}
    </section>
  );
}

function RawMetaPanel({ data }: { data: Preview }) {
  const rows: Array<[string, string | null]> = [
    ['title', data.title],
    ['description', data.description],
    ['og:type', data.ogType],
    ['og:site_name', data.ogSiteName],
    ['og:title', data.ogTitle],
    ['og:description', data.ogDescription],
    ['og:image', data.ogImage],
    ['og:url', data.ogUrl],
    ['twitter:card', data.twitterCard],
    ['twitter:title', data.twitterTitle],
    ['twitter:description', data.twitterDescription],
    ['twitter:image', data.twitterImage],
  ];

  return (
    <details className="mt-8 rounded-2xl border border-line bg-canvas/40 p-4 text-sm">
      <summary className="cursor-pointer font-semibold text-ink">Raw meta tags</summary>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-left">
          <tbody className="divide-y divide-line">
            {rows.map(([k, v]) => (
              <tr key={k}>
                <td className="py-1.5 pr-4 font-mono text-[12px] text-ink-muted whitespace-nowrap align-top">
                  {k}
                </td>
                <td className="py-1.5 break-all text-[12px]">
                  {v === null ? <span className="text-ink-subtle">— missing</span> : v}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}
